import BaseAPI, { recoverFromNotFound, ROUTES, VERSIONS } from './BaseAPI'
import _omitBy from 'lodash-bound/omitBy'
import btoa from 'btoa'
import Hashes from 'jshashes'
import { isEmail } from '../utils/email'
import { isPhone, normalizePhone } from '../utils/phone'

const { VERSION_TWO } = VERSIONS
const { USER_FIND, USER_FIND_ME } = ROUTES

const md5 = new Hashes.MD5()

export default class UsersAPI extends BaseAPI {
  static getSignInAuthHeader(productKey: string, userId: string, password: string) {
    const authString = [productKey, userId, password].join(':')
    const authHash = btoa(authString)
    const authHeader = `XMPP-Simple ${authHash}`

    return authHeader
  }

  getAnonymousAuthHeader(udid: string) {
    const key = this.config.signUpHashKey
    const salt = this.config.signUpHashSalt
    const verifier = [key, salt, udid].join(':')
    const verifierHash = md5.hex(verifier)

    const trueVerifier = [verifierHash.substr(0, 2), verifierHash.substr(verifierHash.length - 2)].join('')

    const authString = [this.config.productKey, key, salt, udid, trueVerifier].join(':')
    const authHash = btoa(authString)

    return `XMPP-Anonymous ${authHash}`
  }

  getSignInAuthHeader(...args) {
    return this.constructor.getSignInAuthHeader(...args)
  }

  async checkLogin(userId, params) {
    const headers = {
      'Authorization': this.getAnonymousAuthHeader(params.udid)
    }

    const query = {
      device: params.udid,
      'tt_product_version': this.config.partnerName,
    }

    if (isPhone(userId)) {
      query.phones = normalizePhone(userId)
    } else {
      query.emails = userId
    }

    const res = await this.httpClient.get('/v4/login_check', {
      headers,
      query,
    })

    return res
  }

  async signUp(params) {
    const headers = {
      'Authorization': this.getAnonymousAuthHeader(params.udid)
    }

    const data = {
      'primary_email': params.email,
      'unique_id': params.email,
      'password': params.password,
      'push_routing': this.config.pushAgent,
      'product': this.config.productKey,
      'country_code': params.countryCode,
      'device_id': params.udid,
      'version': this.config.version
    }

    const res = await this.httpClient.post('/v2/signup', {
      headers,
      data
    })

    return res
  }

  async signIn(headers, params) {
    const data = {
      'device_id': params.udid,
      'metadata': {
        'device_type': 'js_sdk',
        'device_version': __SDK_VERSION__,
        'tt_product_version': this.config.partnerName,
        'user_agent': global.navigator && global.navigator.userAgent,
      },
      'push_routing': this.config.pushAgent,
      'version': this.config.version,
      'want_condensed_replays': this.config.wantCondensedReplays,
      'want_processing_events': this.config.wantCondensedReplays,
    }

    const res = await this.httpClient.post('/v5/login', {
      headers,
      data,
    })

    if (res.data && res.data.settings) {
      const {
        condensed_replays: condensedReplays=false,
        processing_events: processingEvents=false,
      } = res.data.settings

      this.host.configure({ condensedReplays, processingEvents })
    }

    return res
  }

  @recoverFromNotFound({ httpStatusCodes: [400] })
  async lookup(id: string) {
    const { version=VERSION_TWO } = super.getVersion(USER_FIND)

    const data = {
      'display_name': id,
    }

    if (isEmail(id)) {
      data['email_address'] = id
    } else if (isPhone(id)) {
      data['phone_number'] = normalizePhone(id)
    }

    const res = await this.httpClient.post('/:version/user_lookup/:id', {
      urlParams: { id, version },
      data,
    })

    return res.data
  }

  async wipe(id: string) {
    const res = await this.httpClient.post('/v2/wipe', {
      data: {}, // Server assumes that all JSON POSTs with Content-Type will have a payload
      query: { token: id },
    })

    return res
  }

  @recoverFromNotFound()
  async find(id: string, { organizationId }/*: ?{ organizationId?: ?string }*/={}) {
    const { version=VERSION_TWO } = super.getVersion(USER_FIND)

    if (isEmail(id)) {
      id = id.toLowerCase()
    }

    const headers = organizationId ? { 'TT-X-Organization-Key': organizationId } : null
    const res = await this.httpClient.get('/:version/user/:id', {
      headers,
      urlParams: { id, version },
      query: { render_metadata: true },
    })
    return res.data
  }

  async findMe({ organizationId }={}/*: { organizationId?: ?string }*/) {
    const { version=VERSION_TWO } = super.getVersion(USER_FIND_ME)
    const headers = organizationId ? { 'TT-X-Organization-Key': organizationId } : null

    const res = await this.httpClient.get('/:version/user', {
      headers,
      query: { render_metadata: true },
      urlParams: { version },
    })

    return res.data
  }

  async update(id: string, {
    firstName = undefined,
    lastName = undefined,
    displayName = undefined,
    status = undefined,
    dnd = undefined,
    dndText = undefined,
    avatarFile = undefined,
    removeAvatar = undefined,
  }) {
    const files = {}
    if (removeAvatar) files['avatar'] = null
    else if (avatarFile) files['avatar'] = avatarFile

    const data = {
      'first_name': firstName,
      'last_name': lastName,
      'display_name': displayName,
      'status': status,
      'dnd': dnd,
      'dnd_text': dndText,
    }::_omitBy((v, k) => typeof v === 'undefined')

    await this.httpClient.post('/v2/user/:id/settings', {
      urlParams: { id },
      data,
      files
    })

    return {
      id,
      ...((avatarFile && avatarFile.preview) ? { avatarUrl: avatarFile.preview } : null)
    }
  }

  async updatePassword(id: string, currentPassword: string, newPassword: string, productKey: string) {
    const headers = {
      'Authorization': this.getSignInAuthHeader(productKey, id, currentPassword)
    }

    const res = await this.httpClient.post('/v2/user/:id/password', {
      urlParams: { id },
      headers,
      data: {
        'password': newPassword,
      }
    })

    return res
  }

  async setAutoForward(userId: string, organizationId: string, {
    dnd,
    receiverId,
  }: {
    dnd: boolean,
    receiverId: string,
  }) {
    const res = await this.httpClient.post('/v2/user/:userId/organization/:organizationId', {
      urlParams: { userId, organizationId },
      data: {
        dnd,
        'dnd_auto_forward_receiver': receiverId,
      }
    })

    return res
  }

  async removeAutoForward(userId: string, organizationId: string) {
    const res = await this.httpClient.del(
      '/v2/user/:userId/organization/:organizationId/dnd_auto_forward_receiver',
      { urlParams: { userId, organizationId } }
    )
    return res
  }

  async setEulaAccepted(userId: string, organizationId: string, accept: boolean) {
    const res = await this.httpClient.post('/v2/user/:userId/organization/:organizationId', {
      urlParams: { userId, organizationId },
      data: {
        'eula': !accept, // True for displaying the prompt and false to never display it again
      }
    })

    return res
  }

  async patientLoginCheck(linkToken: string, accountToken: string, sessionToken: string) {
    let data = {}
    if (accountToken || sessionToken) {
      data = {
        'session_tokens': [{
          'account_token': accountToken ? accountToken : undefined,
          'session_token': sessionToken ? sessionToken : undefined,
        }]
      }
    }

    const res = await this.httpClient.post('/v5/patient/login_check/:linkToken', {
      urlParams: { linkToken },
      data
    })

    return res.data
  }

  async requestAccessToken(linkToken: string) {
    const res = await this.httpClient.post('/v5/patient/request_access_token/:linkToken', {
      urlParams: { linkToken },
      data: {}
    })

    return res
  }

  async patientLogin(linkToken: string, accessToken: string, dateOfBirth: string, accountToken: string, sessionToken: string) {
    const res = await this.httpClient.post('/v5/patient/login/:linkToken', {
      urlParams: { linkToken },
      data: {
        'auth': [{
          'dob': dateOfBirth ? dateOfBirth : undefined,
          'access_token': accessToken,
        }],
        'session_tokens': [{
          'account_token': accountToken,
          'session_token': sessionToken,
        }]
      }
    })

    return res
  }
}
